import { useState } from "react";
import { Link } from "react-router-dom";
import { toMonthYear, toSlug } from "common";
import { useUser } from "../store";
import { useRemoteSearch } from "../hooks/useRemoteSearch";
import "./DailyReportSearch.scss";

type NoteEntry = {
  id: string;
  date: string;
  createdAt?: string;
  name?: string;
  username: string;
  facility?: string;
  payer?: string;
  claim?: string;
  reportIndex?: number;
  residentName: string;
  note: string;
  months?: string[];
  followUpDate?: string;
};
type NoteSearchResponse = { entries: NoteEntry[]; total: number };

const formatDate = (date: string) => {
  const [year, month, day] = date.split("-");
  return year && month && day ? `${month}/${day}/${year}` : date;
};

const formatTime = (createdAt?: string) =>
  createdAt && !Number.isNaN(Date.parse(createdAt))
    ? new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }).format(new Date(createdAt))
    : undefined;

/** The resident's dashboard with their messages open, when the entry says enough to find the report */
const residentLink = (group: string, entry: NoteEntry) => {
  if (!entry.facility || !entry.payer || !entry.claim || entry.reportIndex === undefined) return undefined;
  const sk = [entry.residentName, entry.claim, entry.reportIndex].map(toSlug).join("#");
  return `/${toSlug(group)}/${toSlug(entry.facility)}/${toSlug(entry.payer)}?report=${encodeURIComponent(sk)}`;
};

/** Finds a note entered for a resident on any day, past or present */
export default function DailyReportSearch({ onSelectDate }: { onSelectDate: (date: string) => void }) {
  const { invokeApi, group } = useUser();
  const [query, setQuery] = useState("");
  const { status, data, query: searched, tooShort } = useRemoteSearch<NoteSearchResponse>({
    invokeApi,
    endpoint: "/searchDailyReportEntries",
    query,
  });
  const entries = data?.entries ?? [];

  return (
    <div className="daily-report-search">
      <div className="daily-report-search-heading">
        <h2>Search notes</h2>
        <p>Find what was noted for a resident on any day.</p>
      </div>
      <div className="control has-icons-left">
        <input
          className="input"
          type="search"
          placeholder="Resident, note text or who wrote it"
          aria-label="Search notes on any day"
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <span className="icon is-small is-left">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
        </span>
      </div>

      {!tooShort && status === "error" && (
        <p className="daily-report-search-message is-error" role="alert">
          The search could not be completed. Try again in a moment.
        </p>
      )}
      {!tooShort && status === "loading" && !entries.length && (
        <p className="daily-report-search-message" role="status">
          Searching…
        </p>
      )}
      {!tooShort && status === "done" && !entries.length && (
        <p className="daily-report-search-message" role="status">
          No notes match “{searched}”.
        </p>
      )}

      {!tooShort && status !== "error" && entries.length > 0 && (
        <>
          <ul className="daily-report-search-results" aria-label="Matching notes" aria-busy={status === "loading"}>
            {entries.map((entry) => {
              const link = residentLink(group, entry);
              const time = formatTime(entry.createdAt);
              return (
                <li key={entry.id} className="daily-report-search-result">
                  <div className="daily-report-search-when">
                    <button type="button" className="daily-report-search-date" onClick={() => onSelectDate(entry.date)} title="Show this day's report">
                      {formatDate(entry.date)}
                    </button>
                    {time && <span>{time}</span>}
                    <span>{entry.name || entry.username}</span>
                  </div>
                  <div className="daily-report-search-who">
                    {link ? <Link to={link}>{entry.residentName}</Link> : <strong>{entry.residentName}</strong>}
                    <span>
                      {[entry.claim, entry.facility && `${entry.facility}${entry.payer ? ` / ${entry.payer.toUpperCase()}` : ""}`].filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <p className="daily-report-search-note">{entry.note}</p>
                  {(entry.months?.length || entry.followUpDate) && (
                    <p className="daily-report-search-meta">
                      {entry.months?.length ? `Months: ${entry.months.map(toMonthYear).join(", ")}` : ""}
                      {entry.months?.length && entry.followUpDate ? " | " : ""}
                      {entry.followUpDate ? `Follow-up date: ${formatDate(entry.followUpDate)}` : ""}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
          {data && data.total > entries.length && (
            <p className="daily-report-search-message">
              Showing the latest {entries.length} of {data.total} matches. Add more words to narrow it down.
            </p>
          )}
        </>
      )}
    </div>
  );
}
